import React, { useMemo } from 'react';

import { Link as MuiLink } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';

import ProtectedData from 'pages/components/ProtectedData';

import useStyles from './styles';

interface WebVulnerability {
    parameter: string;
    url: string;
    position: string;
    description: string;
    cwe: string[];
}

export default function usePathsTableData(webVulnerabilities: WebVulnerability[]) {
    const { classes } = useStyles();

    const data = useMemo(
        () =>
            webVulnerabilities?.map((item) => ({
                ...item,
                cwe: item.cwe
                    .filter((item) => typeof item === 'string')
                    .map((label) => {
                        const id = label.replace(/\D+/g, '');
                        const href = `https://cwe.mitre.org/data/definitions/${id}.html`;
                        return { id, label, href };
                    })
            })) ?? [],
        [webVulnerabilities]
    );

    const columns = useMemo<ColumnDef<(typeof data)[number]>[]>(
        () => [
            {
                header: 'Parameter',
                accessorFn: ({ parameter }) => parameter,
                cell: (info) => info.renderValue(),
                size: 120
            },
            {
                header: 'Position',
                accessorFn: ({ position }) => position,
                cell: (info) => info.renderValue(),
                size: 120
            },
            {
                header: 'Path',
                accessorFn: ({ url }) => url,
                cell: (info) => info.renderValue(),
                minSize: 150
            },
            {
                header: 'Description',
                accessorFn: ({ description }) => description,
                cell: function Description(info) {
                    return <span className={classes.description}>{info.renderValue<string>()}</span>;
                },
                minSize: 150
            },
            {
                header: 'CWE',
                accessorFn: ({ cwe }) => cwe,
                cell: function Cwe(info) {
                    const items = info.getValue<{ id: string; label: string; href: string }[]>();

                    return (
                        <span className={classes.cwe}>
                            {items.map(({ id, label, href }, index) => (
                                <React.Fragment key={id}>
                                    {index === 0 ? null : ', '}
                                    <ProtectedData data={label}>
                                        <MuiLink
                                            href={href}
                                            color={'inherit'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.link}
                                        >
                                            {label}
                                        </MuiLink>
                                    </ProtectedData>
                                </React.Fragment>
                            ))}
                        </span>
                    );
                },
                minSize: 150,
                enableSorting: false
            }
        ],
        [classes.cwe, classes.description, classes.link]
    );

    return { data, columns };
}
